import { zipRecordPermissions } from './recordPermissions'
import type { FgaComputedRelation } from './generated/model'

/**
 * What a caller may do to one request: every `can_*` relation the model defines on `request` but
 * `can_manage_sourcing_granted`, paired with the camelCase key it is reported under. Wider than the
 * vocabulary a permission set configures: `can_edit_po_number` / `can_manage_followers` are
 * record-level participant carve-outs of `edit_metadata` / `manage_participants` rather than set toggles.
 */
export const REQUEST_RECORD_PERMISSIONS = [
  ['canView', 'can_view'],
  ['canEditMetadata', 'can_edit_metadata'],
  ['canEditPoNumber', 'can_edit_po_number'],
  ['canManageParticipants', 'can_manage_participants'],
  ['canManageFollowers', 'can_manage_followers'],
  ['canPause', 'can_pause'],
  ['canReject', 'can_reject'],
  ['canDelete', 'can_delete'],
  ['canEditSubmissions', 'can_edit_submissions'],
  ['canViewDocuments', 'can_view_documents'],
  ['canEditDocuments', 'can_edit_documents'],
  ['canManageDocuments', 'can_manage_documents'],
  ['canManageAssignees', 'can_manage_assignees'],
  ['canSkip', 'can_skip'],
  ['canRollback', 'can_rollback'],
  ['canManageIntegrations', 'can_manage_integrations'],
  ['canManageSourcing', 'can_manage_sourcing'],
  ['canAdminister', 'can_administer'],
  ['canViewTprm', 'can_view_tprm'],
  ['canEditTprm', 'can_edit_tprm'],
  ['canManageTprm', 'can_manage_tprm'],
  ['canViewRisks', 'can_view_risks'],
  ['canEditRisks', 'can_edit_risks'],
  ['canManageRisks', 'can_manage_risks'],
] as const satisfies readonly (readonly [string, FgaComputedRelation<'request'>])[]

export type RequestRecordPermissionKey = (typeof REQUEST_RECORD_PERMISSIONS)[number][0]

export type RequestRecordPermissions = Record<RequestRecordPermissionKey, boolean>

/** The answer for a caller with no access, and the seed every result is built from. */
export const NO_REQUEST_RECORD_PERMISSIONS: RequestRecordPermissions = {
  canView: false,
  canEditMetadata: false,
  canEditPoNumber: false,
  canManageParticipants: false,
  canManageFollowers: false,
  canPause: false,
  canReject: false,
  canDelete: false,
  canEditSubmissions: false,
  canViewDocuments: false,
  canEditDocuments: false,
  canManageDocuments: false,
  canManageAssignees: false,
  canSkip: false,
  canRollback: false,
  canManageIntegrations: false,
  canManageSourcing: false,
  canAdminister: false,
  canViewTprm: false,
  canEditTprm: false,
  canManageTprm: false,
  canViewRisks: false,
  canEditRisks: false,
  canManageRisks: false,
}

export const toRequestRecordPermissions = (allowed: readonly boolean[]): RequestRecordPermissions =>
  zipRecordPermissions('request', REQUEST_RECORD_PERMISSIONS, NO_REQUEST_RECORD_PERMISSIONS, allowed)
